import React, { useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Box, Button, Card, CardContent, CircularProgress, Grid, TextField, Typography, Divider,
} from '@mui/material';

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function MatchCards() {
  const [matchId, setMatchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [error, setError] = useState('');

  const fetchCard = async () => {
    if (!matchId) return;
    setLoading(true);
    setError('');
    setCard(null);
    try {
      const chatUrl = (BASE_URL || '').replace(/\/api\/?$/, '').replace(':3000', ':3001');
      const res = await fetch(`${chatUrl}/admin/ai-chat/${matchId}/card`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success && data.data) {
        setCard(data.data);
      } else {
        setError(data.error || 'No card data');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
        <Typography variant="h5" fontWeight={700} gutterBottom>Shareable Match Cards</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate structured data for shareable match summary cards. Enter a match ID to preview.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Match ID"
            value={matchId}
            onChange={e => setMatchId(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
          <Button variant="contained" onClick={fetchCard} disabled={loading || !matchId}>
            {loading ? <CircularProgress size={20} /> : 'Generate Card'}
          </Button>
        </Box>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {card && (
          <Card elevation={3} sx={{ maxWidth: 480, borderRadius: 3, overflow: 'hidden' }}>
            {/* Card Preview */}
            <Box sx={{ bgcolor: '#1a1a2e', color: 'white', p: 2.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>MATCH RESULT</Typography>
              <Typography variant="h6" fontWeight={700}>{card.result}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>{card.venue}</Typography>
            </Box>

            <CardContent>
              {/* Scores */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight={700}>{card.team_a?.name}</Typography>
                  <Typography variant="h6">{card.team_a?.score}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight={700}>{card.team_b?.name}</Typography>
                  <Typography variant="h6">{card.team_b?.score}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 1.5 }} />

              {/* Top Performers */}
              {card.top_batters?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">TOP BATTERS</Typography>
                  {card.top_batters.map((b, i) => (
                    <Typography key={i} variant="body2">
                      <strong>{b.name}</strong> — {b.stat} ({b.team})
                    </Typography>
                  ))}
                </Box>
              )}

              {card.top_bowlers?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">TOP BOWLERS</Typography>
                  {card.top_bowlers.map((b, i) => (
                    <Typography key={i} variant="body2">
                      <strong>{b.name}</strong> — {b.stat} ({b.team})
                    </Typography>
                  ))}
                </Box>
              )}

              {card.key_partnership && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">KEY PARTNERSHIP</Typography>
                  <Typography variant="body2">
                    <strong>{card.key_partnership.runs} runs</strong> — {(card.key_partnership.batters || []).join(' & ')} ({card.key_partnership.team})
                  </Typography>
                </Box>
              )}

              {card.mom && (
                <Box>
                  <Typography variant="caption" color="text.secondary">PLAYER OF THE MATCH</Typography>
                  <Typography variant="body2"><strong>{card.mom.name || card.mom}</strong></Typography>
                </Box>
              )}
            </CardContent>

            <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">dugoutlive.com</Typography>
            </Box>
          </Card>
        )}

        {card && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Raw JSON:</Typography>
            <Box component="pre" sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.75rem', overflow: 'auto', maxHeight: 300 }}>
              {JSON.stringify(card, null, 2)}
            </Box>
          </Box>
        )}
        </div>
      </div>
    </Component>
  );
}

export default MatchCards;
